// client/src/utils/offlineReadCache.js
//
// THE LAST GOOD ANSWER TO EVERY READ.
//
// Offline writing is useless without offline reading, and the dependency runs
// one way: you cannot take a register without the class list, enter a mark
// without the students, print a receipt without the fee, or issue an ID card
// without the pupil. Queuing a write into an app that cannot show you anything
// is a blank screen with a save button.
//
// The service worker already caches some of this, but only what the user
// happened to open, only for GET requests it recognises, and with no knowledge
// of academic-year scope. This store is explicit: it keeps the parsed body of
// every successful API read, keyed by URL AND the scope it was read under —
// `/api/classes` in 2025/2026 secondary is not the same list as in 2026/2027
// nursery, and serving one for the other is the kind of error nobody catches
// until a report is wrong.
//
// The native half of this lives in packages/core/src/offline/readCache.js. The
// storage differs (IndexedDB here, expo-sqlite there) but the keying, the
// expiry and the cap are the same, and must stay so.
//
// It is a CACHE, not a database. Nothing here is the only copy of anything.

const DB_NAME = 'ameca-offline-reads';
const DB_VERSION = 1;
const STORE = 'reads';

/** A month. Long enough to cover a holiday, short enough not to be a museum. */
export const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Entries kept before the oldest are dropped. */
export const MAX_ENTRIES = 600;

// Mirrors SCOPE_HEADERS in outboxIdentity — the headers that change what a URL
// means.
const SCOPE_HEADERS = [
  'X-School-Id',
  'X-Scope-School-Id',
  'X-Academic-Year-Id',
  'X-Active-Phase',
  'X-Active-Phase-Id',
];

/** The cache key: URL plus the scope headers in force, sorted so it is stable. */
export function cacheKey(url, headers = {}) {
  const scope = SCOPE_HEADERS
    .map((h) => {
      const v = headers[h] ?? headers[h.toLowerCase()] ?? '';
      return v ? `${h}=${v}` : null;
    })
    .filter(Boolean)
    .sort()
    .join('&');
  return scope ? `${url}||${scope}` : String(url);
}

function hasIndexedDB() {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch (_) {
    return false;
  }
}

function openDB() {
  return new Promise((resolve, reject) => {
    if (!hasIndexedDB()) { reject(new Error('IndexedDB not available')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'key' });
        os.createIndex('savedAt', 'savedAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function readCacheSupported() {
  return hasIndexedDB();
}

/** Remember a successful GET. Failing to cache is never an error to the caller. */
export async function putRead(url, headers, body) {
  if (!hasIndexedDB()) return false;
  try {
    const db = await openDB();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({
        key: cacheKey(url, headers),
        url: String(url),
        body,
        savedAt: Date.now(),
      });
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
    // After the write, so the cost stays off the path the user is waiting on.
    trim().catch(() => {});
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * The last good answer, with how old it is.
 *
 * `age` is returned rather than swallowed so the UI can say "as of this
 * morning" — data of unknown age is what makes people distrust an offline mode.
 */
export async function getRead(url, headers) {
  if (!hasIndexedDB()) return null;
  try {
    const db = await openDB();
    const hit = await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(cacheKey(url, headers));
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
    if (!hit) return null;
    const age = Date.now() - (hit.savedAt || 0);
    if (age > MAX_AGE_MS) return null;
    return { body: hit.body, savedAt: hit.savedAt, age };
  } catch (_) {
    return null;
  }
}

async function allEntries() {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

/** Drop what has expired, then the oldest of whatever is left over the cap. */
export async function trim() {
  if (!hasIndexedDB()) return 0;
  try {
    const all = await allEntries();
    const now = Date.now();
    const expired = all.filter((e) => now - (e.savedAt || 0) > MAX_AGE_MS);
    const live = all
      .filter((e) => now - (e.savedAt || 0) <= MAX_AGE_MS)
      .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
    const drop = [...expired, ...live.slice(MAX_ENTRIES)].map((e) => e.key);
    if (!drop.length) return 0;
    const db = await openDB();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      const os = tx.objectStore(STORE);
      drop.forEach((k) => os.delete(k));
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
    return drop.length;
  } catch (_) {
    return 0;
  }
}

/** Everything, for a "what can I work on offline?" screen. */
export async function cachedReads() {
  if (!hasIndexedDB()) return [];
  try {
    return (await allEntries())
      .map((e) => ({ url: e.url, savedAt: e.savedAt }))
      .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
  } catch (_) {
    return [];
  }
}

/** On sign-out. One user's cached reads must never answer the next user. */
export async function clearReadCache() {
  if (!hasIndexedDB()) return false;
  try {
    const db = await openDB();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch (_) {
    return false;
  }
}
